import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useEffect, useCallback, lazy, Suspense } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import IdleWarningDialog from "./components/common/IdleWarningDialog";
import ProtectedRoute from "./components/common/ProtectedRoute";
import useIdleTimer from "./hooks/useIdleTimer";
import AboutUs from "./pages/About_Us/AboutUs";
import AcceptedProposals from "./pages/accepted-proposals/AcceptedProposals";
import ViewProposalDetails from "./pages/accepted-proposals/ViewProposalDetails";
import Application from "./pages/application/Application";
import ApplicationHistory from "./pages/application-history/ApplicationHistory";
import ApplyLoan from "./pages/apply-loan/ApplyLoan";
import Careers from "./pages/Careers/Careers";
import ContactUs from "./pages/ContactUs/ContactUs";
import FAQ from "./pages/FAQ/FAQ";
import Footer from "./pages/Footer/Footer";
import Header from "./pages/Header/Header";
import Home from "./pages/Home/Home";
import HowItWorks from "./pages/Home/HowItWorks";
import NextStepCard from "./pages/Home/NextStepCard";
import LoanEligibilityCalculator from "./pages/LoanEligibilityCalculator/LoanEligibilityCalculator";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import TestimonialsPage from "./pages/Testimonials/TestimonialsPage";
import { setUser } from "./ReduxStore/UserSlice";
import {
  ensureAuthTokens,
  preserveAuthTokens,
  restoreAuthTokens,
} from "./components/common/Util/Util";
import BlogPost from "./pages/Blog/BlogPost";
import Knowledge from "./pages/Knowledge/Knowledge.js";
import KnowledgePost from "./pages/Knowledge/KnowledgePost";
import "./components/common/Util/Util";
import AddProfilePage from "./components/profile/AddProfilePage.js";
import ConsultantProposals from "./pages/proposals/ConsultantProposals";
import ConsultantProposalDetails from "./pages/proposals/ConsultantProposalDetails";
import ConsultantCreateProposal from "./pages/proposals/ConsultantCreateProposal";
// import AboutUs from "./pages/About_Us/AboutUs.js";
// import Careers from "./pages/Careers/Careers.js";
import SMEAdvisory from "./pages/SMEAdvisory/SMEAdvisory.js";
import Blog from "./pages/Blog/Blog.js";

// ✅ Lazy-loaded (only dashboard-related)
const DashboardLayout = lazy(() =>
  import("./components/layout/DashboardLayout")
);
const Dashboard = lazy(() => import("./components/dashboard/Dashboard"));
const Profile = lazy(() => import("./pages/profile/Profile"));
// const ApplyLoan = lazy(() => import("./pages/apply-loan/ApplyLoan"));
const ResumeApplication = lazy(() =>
  import("./pages/resume-application/ResumeApplication")
);
// const AcceptedProposals = lazy(() =>
//   import("./pages/accepted-proposals/AcceptedProposals")
// );
// const ViewProposalDetails = lazy(() =>
//   import("./pages/accepted-proposals/ViewProposalDetails")
// );
const OrganizationDetails = lazy(() =>
  import("./pages/profile/OrganizationDetails.jsx")
);
const DocumentUploadPage = lazy(() =>
  import("./pages/document-upload/DocumentUploadPage")
);
const NewProposals = lazy(() => import("./pages/proposals/NewProposals"));
const IndicativeProposals = lazy(() =>
  import("./pages/proposals/IndicativeProposals")
);
const RejectedProposals = lazy(() =>
  import("./pages/proposals/RejectedProposals")
);
const ProposalDetails = lazy(() => import("./pages/proposals/ProposalDetails"));
const CreateLoanProposal = lazy(() =>
  import("./pages/proposals/CreateLoanProposal")
);
const MessagesPage = lazy(() => import("./pages/messages/MessagesPage"));
// const ApplicationHistory = lazy(() =>
//   import("./pages/application-history/ApplicationHistory")
// );
const NotificationSettings = lazy(() =>
  import("./pages/notification-settings/NotificationSettings")
);
const ApplicationTracker = lazy(() =>
  import("./pages/application-history/ApplicationTracker.js")
);

const theme = createTheme({
  typography: {
    fontFamily: "'Inter', 'Open Sans', sans-serif",
    h1: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 700,
    },
    h2: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 700,
    },
    h3: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 700,
    },
    h4: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 700,
    },
    h5: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 600,
    },
    h6: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 600,
    },
    subtitle1: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 500,
    },
    body1: {
      fontFamily: "'Inter', sans-serif",
    },
    body2: {
      fontFamily: "'Inter', sans-serif",
    },
    button: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 600,
      textTransform: "none",
    },
  },
  palette: {
    primary: {
      main: "#0052CC",
      dark: "#003E99",
    },
    secondary: {
      main: "#0C53B7",
    },
    text: {
      primary: "#212B36",
      secondary: "#637381",
    },
    background: {
      default: "#F5F6F7",
      paper: "#FFFFFF",
    },
  },
  components: {
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderRadius: 10,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          boxShadow: "none",
          borderRadius: 8,
          "&:hover": {
            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
          },
        },
      },
    },
  },
});

function App() {
  const dispatch = useDispatch();
  const location = useLocation();
  const isAuthenticated = useSelector((state) => state.user?.user?.jwtToken);
  const { showWarning, resetTimerManually } = useIdleTimer();
  const user = useSelector((state) => state.user);

  useEffect(() => {
    if (!user || !user.user || !user.user.jwtToken) {
      if (localStorage.getItem("jwtToken")) {
        const userObject = localStorage.getItem("user");
        if (userObject) {
          dispatch(setUser(JSON.parse(userObject)));
        }
      }
    }
  }, [dispatch, user]);

  const shouldShowHeaderAndFooter = !(
    location?.pathname === "/register" ||
    location?.pathname === "/otp-verification" ||
    location?.pathname === "/welcome" ||
    location?.pathname.startsWith("/dashboard") ||
    location?.pathname === "/profile" ||
    location?.pathname === "/application-history" ||
    location?.pathname === "/notification-settings" ||
    location?.pathname === "/apply-loan" ||
    location?.pathname === "/resume-application" ||
    location?.pathname === "/accepted-offers" ||
    location?.pathname.startsWith("/proposal-details") ||
    location?.pathname === "/calculators" ||
    location?.pathname === "/dashboard/how-it-works" ||
    location?.pathname === "/dashboard/contact-us" ||
    location?.pathname.startsWith("/proposals") ||
    location?.pathname.startsWith("/proposals/") ||
    location?.pathname === "/messages" ||
    location?.pathname === "/organization-details" ||
    location?.pathname === "/track-application" ||
    location?.pathname === "/document-upload"
    // location?.pathname.startsWith("/application")
  );

  // useEffect(() => {
  //   fetch("https://api.ipify.org?format=json")
  //     .then((response) => response.json())
  //     .then((data) => dispatch(setIp(data?.ip)))
  //     .catch((error) => console.error("Error fetching IP:", error));
  // }, [dispatch]);

  // useEffect(() => {
  //   const error = (err) => {
  //     console.error("Error fetching location:", err);
  //     dispatch(setLoactionFlag(false));
  //   };

  //   const success = (position) => {
  //     const coordinates = {
  //       lat: position.coords.latitude ? position.coords.latitude : "",
  //       lng: position.coords.longitude ? position.coords.latitude : "",
  //     };
  //     dispatch(setCordinates(coordinates));
  //   };

  //   const searchParams = new URLSearchParams(location.search);
  //   let qlat = searchParams.get("latitude");
  //   let qlng = searchParams.get("longitude");
  //   let pathname = {
  //     coords: {
  //       latitude: qlat ? Number(qlat) : null,
  //       longitude: qlng ? Number(qlng) : null,
  //     },
  //   };

  //   if (
  //     pathname.coords.latitude &&
  //     pathname.coords.longitude 
  //   ) {
  //     success(pathname);
  //     dispatch(setLoactionFlag(false));
  //   } else {
  //     navigator.geolocation.getCurrentPosition(success, error);
  //   }
  //   dispatch(setLoactionFlag(false));
  // }, [dispatch, location.search]);

  const ensureAuthTokensExist = useCallback(() => {
    if (user?.user?.jwtToken) {
      ensureAuthTokens(user);
    }
  }, [user]);

  useEffect(() => {
    if (isAuthenticated && user?.user.UserType) {
      ensureAuthTokensExist();
    }
  }, [
    location?.pathname,
    isAuthenticated,
    ensureAuthTokensExist,
    user?.user.UserType,
  ]);

  return (
    <ThemeProvider theme={theme}>
      {shouldShowHeaderAndFooter && <Header />}
      <div
        style={{
          overflowX: "hidden",
          // marginTop: shouldShowHeaderAndFooter ? '0' : '-60px',
        }}
      >
        <ToastContainer />
        <IdleWarningDialog
          open={showWarning}
          onContinue={() => {
            try {
              preserveAuthTokens();
              resetTimerManually();
              restoreAuthTokens(user);

              setTimeout(() => {
                restoreAuthTokens(user);
              }, 100);
            } catch (error) {
              console.error(
                "[SESSION-CONTINUE] Error in token preservation:",
                error
              );
            }
          }}
        />

          <Routes>
            <Route
              path="/"
              element={
                <>
                  <Home setTabIndex={() => {}} />
                  <NextStepCard />
                </>
              }
            />
            <Route
              path="/about-us"
              element={
                <>
                  <AboutUs />
                  <NextStepCard />
                </>
              }
            />
            <Route
              path="/how-it-works"
              element={
                <>
                  <HowItWorks />
                  <NextStepCard />
                </>
              }
            />
            <Route
              path="/how-it-works/lender"
              element={
                <>
                  <HowItWorks />
                  <NextStepCard />
                </>
              }
            />
              <Route
              path="/how-it-works/borrower"
              element={
                <>
                  <HowItWorks />
                  <NextStepCard />
                </>
              }
            />
              <Route
              path="/how-it-works/consultant"
              element={
                <>
                  <HowItWorks />
                  <NextStepCard />
                </>
              }
            />
            <Route
              path="/sme-advisory"
              element={
                <>
                  <SMEAdvisory />
                  <NextStepCard />
                </>
              }
            />
            <Route
              path="/careers"
              element={
                <>
                  <Careers />
                  <NextStepCard />
                </>
              }
            />
            <Route path="/contact-us" element={<ContactUs />} />
            <Route path="/calculator" element={<LoanEligibilityCalculator />} />
            <Route path="/add-profile" element={<AddProfilePage />} />

            {/* Other existing routes */}
            <Route path="/testimonials" element={<TestimonialsPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/knowledge" element={<Knowledge />} />
            <Route path="/knowledge/:slug" element={<KnowledgePost />} />
            <Route
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/profile" element={<Profile />} />
            {/* <Route path="/application" element={<Application />} />
            <Route path="/application/new-application" element={<ApplyLoan />} />
            <Route path="/application/resume-application" element={<ResumeApplication />} />
            <Route path="/application/submitted-application" element={<ApplicationHistory />} /> */}
              <Route path="/apply-loan" element={<ApplyLoan />} />
              <Route path="/resume-application" element={<ResumeApplication />} />
              <Route path="/accepted-offers" element={<AcceptedProposals />} />
              <Route
                path="/organization-details"
                element={<OrganizationDetails />}
              />
              <Route path="/document-upload" element={<DocumentUploadPage />} />
              <Route
                path="/proposal-details/:offerId"
                element={<ViewProposalDetails />}
              />
              <Route path="/proposals/new" element={<NewProposals />} />
              <Route
                path="/proposals/indicative"
                element={<IndicativeProposals />}
              />
              <Route path="/proposals/rejected" element={<RejectedProposals />} />
              <Route
                path="/proposals/details/:proposalId"
                element={<ProposalDetails />}
              />
              <Route
                path="/proposals/create/:proposalId"
                element={<CreateLoanProposal />}
              />
            <Route path="/proposals" element={<ConsultantProposals />} />
            <Route
              path="/proposals/consultant/details/:proposalId"
              element={<ConsultantProposalDetails />}
            />
            <Route
              path="/proposals/consultant/create/:proposalId"
              element={<ConsultantCreateProposal />}
            />
              {/* <Route path="/transactions" element={<TransactionsPage />} />
            <Route path="/transactions/:txnId" element={<TransactionDetails />} /> */}
              <Route path="/messages" element={<MessagesPage />} />
              <Route
                path="/calculators"
                element={<LoanEligibilityCalculator />}
              />
              <Route
                path="/application-history"
                element={<ApplicationHistory />}
              />
              <Route
                path="/notification-settings"
                element={<NotificationSettings />}
              />
              <Route path="/track-application" element={<ApplicationTracker />} />
              <Route path="/dashboard/how-it-works/lender" element={<HowItWorks />} />
              <Route path="/dashboard/how-it-works/borrower" element={<HowItWorks />} />
              <Route path="/dashboard/how-it-works/consultant" element={<HowItWorks />} />
              <Route path="/dashboard/contact-us" element={<ContactUs />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Route>
          </Routes>
        {shouldShowHeaderAndFooter && <Footer />}
      </div>
    </ThemeProvider>
  );
}

export default App;
